import React from 'react';
import { Box, Divider, IconButton, Typography, alpha } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { formatBookingCurrency } from '../../utils/bookingPaymentSummary';
import { buildExtraChargesSummary } from '../../utils/extraChargesGstUtils';

function DetailRow({ label, value, isDark, valueColor, bold = false, indent = false }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 1.5,
        mb: 0.5,
        pl: indent ? 1 : 0,
      }}
    >
      <Typography
        variant="body2"
        sx={{
          color: 'text.secondary',
          fontSize: indent ? '0.8125rem' : undefined,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: bold ? 800 : 700,
          color: valueColor || 'text.primary',
          fontSize: indent ? '0.8125rem' : undefined,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

/**
 * Read-only extra charge line items with base / GST / total calculation.
 */
export default function BookingExtraChargesBreakdown({
  items = [],
  amount = 0,
  baseAmount = 0,
  gstAmount = 0,
  note = '',
  currency = 'INR',
  isDark = false,
  accent = '#0f766e',
  compact = false,
  showHeading = true,
  /** When true, show amount-only rows (no Base / GST / Line total). */
  amountOnly = false,
  onEditItem,
  onRemoveItem,
  removeDisabled = false,
}) {
  const summary = buildExtraChargesSummary({
    existingItems: items,
    existingAmount: amount,
    existingBaseAmount: baseAmount,
    existingGstAmount: gstAmount,
    existingNote: note,
  });

  if (!summary.showSummary) return null;

  const { existingRows, existingTotals } = summary;
  const hasGst = existingTotals.gstAmount > 0;
  const hasMultipleLines = existingRows.length > 1;
  const accentColor = isDark ? '#ffcc80' : '#e65100';
  const gstColor = isDark ? '#90caf9' : '#1565c0';
  const canEdit = typeof onEditItem === 'function';
  const canRemove = typeof onRemoveItem === 'function';

  const renderActions = (row) => {
    if (!canEdit && !canRemove) return null;
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.1 }}>
        {canEdit ? (
          <IconButton
            size="small"
            onClick={() => onEditItem(row)}
            disabled={removeDisabled}
            aria-label={`Edit ${row.reason}`}
          >
            <EditOutlinedIcon fontSize="small" />
          </IconButton>
        ) : null}
        {canRemove ? (
          <IconButton
            size="small"
            onClick={() => onRemoveItem(row)}
            disabled={removeDisabled}
            aria-label={`Remove ${row.reason}`}
          >
            <DeleteOutlineIcon fontSize="small" />
          </IconButton>
        ) : null}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        mt: compact ? 0.5 : 0.75,
        mb: compact ? 0.5 : 0.75,
        p: compact ? 1 : 1.1,
        borderRadius: 2,
        border: `1px dashed ${alpha(accent, isDark ? 0.35 : 0.28)}`,
        backgroundColor: isDark ? alpha(accent, 0.08) : alpha(accent, 0.04),
      }}
    >
      {showHeading ? (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            fontWeight: 800,
            color: accent,
            mb: 0.85,
            letterSpacing: '0.02em',
            textTransform: 'uppercase',
          }}
        >
          Extra Charges
        </Typography>
      ) : null}

      {existingRows.map((row, index) => (
        <Box
          key={row.id || `${row.reason}-${index}`}
          sx={{
            mb: index < existingRows.length - 1 ? (amountOnly ? 0.65 : 1) : hasMultipleLines ? 0.75 : 0,
            pb: index < existingRows.length - 1 ? (amountOnly ? 0.55 : 0.85) : 0,
            borderBottom:
              index < existingRows.length - 1
                ? `1px solid ${alpha(accent, isDark ? 0.16 : 0.12)}`
                : 'none',
          }}
        >
          {amountOnly ? (
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 0.75,
              }}
            >
              <Typography
                variant="body2"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  fontSize: compact ? '0.8125rem' : undefined,
                }}
              >
                {row.reason}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 800,
                    color: accentColor,
                    fontSize: compact ? '0.8125rem' : undefined,
                    whiteSpace: 'nowrap',
                  }}
                >
                  {formatBookingCurrency(row.base || row.total, currency)}
                </Typography>
                {renderActions(row)}
              </Box>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: 0.75,
                  mb: 0.45,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    fontWeight: 700,
                    color: 'text.primary',
                    fontSize: compact ? '0.8125rem' : undefined,
                  }}
                >
                  {row.reason}
                </Typography>
                <Box sx={{ mt: -0.5, mr: -0.5 }}>
                  {renderActions(row)}
                </Box>
              </Box>
              <DetailRow
                label="Base"
                value={formatBookingCurrency(row.base, currency)}
                isDark={isDark}
                indent
              />
              {row.gstAmount > 0 ? (
                <DetailRow
                  label={row.gstPercent > 0 ? `GST (${row.gstPercent}%)` : 'GST'}
                  value={formatBookingCurrency(row.gstAmount, currency)}
                  isDark={isDark}
                  valueColor={gstColor}
                  indent
                />
              ) : null}
              <DetailRow
                label="Line total"
                value={formatBookingCurrency(row.total, currency)}
                isDark={isDark}
                valueColor={accentColor}
                bold
                indent
              />
            </>
          )}
        </Box>
      ))}

      {hasMultipleLines ? (
        <>
          <Divider sx={{ my: 0.75, borderColor: alpha(accent, isDark ? 0.2 : 0.14) }} />
          {amountOnly ? (
            <DetailRow
              label="Extra charges total"
              value={formatBookingCurrency(existingTotals.base || existingTotals.total, currency)}
              isDark={isDark}
              valueColor={accentColor}
              bold
            />
          ) : (
            <>
              <DetailRow
                label="Extra charges base"
                value={formatBookingCurrency(existingTotals.base, currency)}
                isDark={isDark}
              />
              {hasGst ? (
                <DetailRow
                  label="Extra charges GST"
                  value={formatBookingCurrency(existingTotals.gstAmount, currency)}
                  isDark={isDark}
                  valueColor={gstColor}
                />
              ) : null}
              <DetailRow
                label="Extra charges total"
                value={formatBookingCurrency(existingTotals.total, currency)}
                isDark={isDark}
                valueColor={accentColor}
                bold
              />
            </>
          )}
        </>
      ) : null}
    </Box>
  );
}
